/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { FormDefinitionWrapper } from './form/services/form';
import { isNullOrUndefined } from './form';
import { createStyles, makeStyles, Container, Divider, Grid, Hidden, List, Paper, TablePagination, Theme, Tooltip, Typography, ListItem, ListItemText } from "@material-ui/core";
import Error from "@material-ui/icons/Block";
import Certificate from "@material-ui/icons/CardMembership";
import Email from "@material-ui/icons/Email";
import Phone from "@material-ui/icons/Phone";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Warning from "@material-ui/icons/Warning";
// import { createStyles, makeStyles } from "@material-ui/styles";
import moment, { Moment } from "moment";
import React, { FC, useEffect, useMemo, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router";
import { createSelector } from "reselect";
import { AppState, nearestDeadline, syncSystem, FollowUp, getFollowUpsForFacility } from "../../redux";
import { drawerWidth } from "../container/AppHeader";
import FollowUpListItem from "./FollowUpListItem";
import { Alert } from '@material-ui/lab';
import LoadingIndicator from './form/components/LoadingIndicator';

interface DueFollowUp {
  daysUntilDue: number;
  priority: number;
  type: string;
}

interface SummarySectionProps {
  title: string;
  children: React.ReactNode
}

const SummarySection: FC<SummarySectionProps> = props => {
  // not sure why i have to cast this to any. Seems the same as in all other components.
  const classes = useStyles(props as any);

  return (
    <div className={classes.summarySection}>
      <Typography variant="subtitle2" className={classes.summarySectionTitle}>
        {props.title}
      </Typography>
      <div className={classes.summarySectionContents}>
        {props.children}
      </div>
    </div>
  )
};


// this isn't an immutable map, whereas followups is
export const selectFacility = (facilityId: string) => createSelector(
  (state: AppState) => state.facilities.items,
  items => items[facilityId]
);
export const selectFollowUps = (facilityId: string) => createSelector(
  (state: AppState) => state.followUps.items,
  items => items.get(facilityId)
);
const selectFetchingFollowUpsState = createSelector(
  (state: AppState) => state.followUps.fetchingAll || state.followUps.fetchingForFacility,
  fetching => fetching
);

const selectForms = createSelector(
  (state: AppState) => state.forms.items,
  items => Object.entries(items)
    .filter(([type, versions]) =>['mcap', 'cip', 'iap'].includes(type))
    .map(function getLatestForType([type, versions]) {
      const latestVersion = Object.keys(versions)
        .map(version => Number.parseInt(version))
        .sort((a, b) => a - b).pop()!;

      return {
        type, form: versions[latestVersion]!
      }
    }).reduce((acc, curr) => {
      return { ...acc, [curr.type]: curr.form }
    }, {} as { [type: string]: FormDefinitionWrapper })
)

interface FollowUpFacilityViewProps { }

const FollowUpFacilityView: FC<FollowUpFacilityViewProps> = props => {
  // not sure why i have to cast this to any. Seems the same as in all other components.
  const classes = useStyles(props as any);

  const match = useRouteMatch<{ facilityId: string }>("/follow-ups/:facilityId")!;
  const facilityId = match.params.facilityId;

  const fetchingFollowUps = useSelector(selectFetchingFollowUpsState);
  const facility = useSelector(selectFacility(facilityId));
  const followUps = useSelector(selectFollowUps(facilityId));
  const [ totalFollowUps, setTotalFollowUps ] = useState(0);
  // const errorFetching = useSelector();
  const dispatch = useDispatch();

  useEffect(() => {
    setTotalFollowUps(followUps?.length ?? -1);
  }, [followUps])

  useEffect(
    function refresh() {
      dispatch(syncSystem());
      dispatch(getFollowUpsForFacility.request(facilityId));
    },
    [dispatch, facilityId]
  );
// todo filter facilities in a custom selector (createSelector)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [facilityStatus, setFacilityStatus] = useState<string | null>(null);
  const [overdueCips, setOverdueCips] = useState<number>(0);
  const [overdueMcaps, setOverdueMcaps] = useState<number>(0);
  const [overdueIaps, setOverdueIaps] = useState<number>(0);
  const [statusIcon, setStatusIcon] = useState<any>(null);
  const [statusText, setStatusText] = useState('');
  const [visibleFollowUps, setVisibleFollowUps] = useState<FollowUp[]>([]);

  useEffect(() => {
    switch (facilityStatus) {
      case 'active':
        setStatusText('Certified');
        setStatusIcon(<Certificate />);
        break;
      case 'probation':
        setStatusText('Conditionally Certified');
        setStatusIcon(<Warning className={classes.warning} />);
        break;
      case 'suspended':
        setStatusText('Conditionally Decertified');
        setStatusIcon(<Error className={classes.error} />);
        break;
    }
  }, [facilityStatus, classes.warning, classes.error])

  useEffect(() => {
    if (!followUps) {
      return;
    }

    const f = followUps.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    setVisibleFollowUps(f);
  }, [followUps, page, rowsPerPage])

  useEffect(() => {
    if (!facility) {
      return;
    }
    setFacilityStatus(facility.status);
  }, [facility])

  useEffect(() => {
    let oc = 0, om = 0, oi = 0;

    const now = moment();
    for (let plan of followUps ?? []) {
      if (now.isAfter(plan.type === 'iap' ? nearestDeadline(plan.data) : plan.data.due)) {
        switch (plan.type) {
          case 'cip': oc++; break;
          case 'mcap': om++; break;
          case 'iap': oi++; break;
          default: break;
        }
      }
    }

    setOverdueCips(oc);
    setOverdueMcaps(om);
    setOverdueIaps(oi);
  }, [followUps]);

  /**
   * IAPs are given a higher priority. IAPs immediately conditionally decertify a facility if they are overdue. MCAPs and CIPs will
   * conditionally *certify* a facility when 30 days overdue, and conditionally *decertify* a facility when 60 days overdue.
   */
  const getPriorityForFollowUp = (followUp: FollowUp): DueFollowUp => {
    let daysUntilDue: number;

    if (followUp.type === 'iap') {
      daysUntilDue = moment(nearestDeadline(followUp.data)).diff(moment(), 'd');

      return {
        daysUntilDue,
        priority: -daysUntilDue + 60,
        type: 'iap'
      };
    } else {
      daysUntilDue = moment(followUp.data.due).diff(moment(), 'd');

      return {
        daysUntilDue,
        priority: -daysUntilDue,
        type: followUp.type
      };
    }
  }

  const [ highestPriorityFollowUp, setHighestPriorityFollowUp ] = useState<DueFollowUp | null>(null);
  useEffect(() => {

    const prioritizedFollowUps = followUps?.map(f => getPriorityForFollowUp(f)) || [];

    const nearest = prioritizedFollowUps.sort((a, b) => b.priority - a.priority)?.[0];

    if (nearest) {
      setHighestPriorityFollowUp(nearest);
    } else {
      setHighestPriorityFollowUp(null);
    }

  }, [followUps]);

  const statusMessage = useMemo(() => {
    if (isNullOrUndefined(highestPriorityFollowUp)) {
      return `There are no action plans due within the next 30 days.`;
    }

    const { daysUntilDue, type } = highestPriorityFollowUp!;

    if (daysUntilDue > 30) {
      return `There are no action plans due within the next 30 days.`;
    } else if (type === 'iap') {
      if (daysUntilDue < 0) {
        return `This facility is conditionally decertified. Please contact dairyfarm@nmpf.org for more information.`;
      } else {
        if (daysUntilDue === 0) {
          return `This facility will be conditionally decertified tomorrow.`;
        }
        return `You have ${daysUntilDue} days to resolve this action plan until this facility is conditionally decertified.`;
      }
    } else if (daysUntilDue > 0 && daysUntilDue <= 30) {
      return `You have ${daysUntilDue} days to resolve this action plan until this facility is conditionally certified.`;
    } else if (daysUntilDue >= -60 && daysUntilDue < 0) {
      return `You have ${60 + daysUntilDue} days to resolve this action plan until this facility is conditionally decertified.`;
    } else if (daysUntilDue < -60) {
      return `This facility is conditionally decertified. Please contact dairyfarm@nmpf.org for more information.`;
    } else if (daysUntilDue === 0) {
      return `This facility will be conditionally certified tomorrow.`;
    }

  }, [highestPriorityFollowUp]);

  const forms = useSelector(selectForms);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // TODO: Add the status icon corresponding to the curent facility status
  // const StatusIcon = useMemo(() => {

  // }, [facility?.status])

  const facilitySummary = (
    <div className={classes.summary}>
      {facility && (
        <>
          <Grid container >
            <Grid id="facility-name" item xs={12} sm={4} md={3}>
              <Typography variant="h6" className={classes.facilityPrimary}>
                {facility.name}
              </Typography>
              <Typography variant="body2" className={classes.facilitySecondary}>
                {facility.farm.name}
              </Typography>
              {facility.coop && (
                <Typography variant="body2" className={classes.facilitySecondary}>
                  {facility.coop.name}
                </Typography>
              )}
            </Grid>

            <Hidden smUp>
              <Grid item xs={12}>
                <Divider variant="middle" className={classes.divider} />
              </Grid>
            </Hidden>

            <Grid item xs={5} sm={4} md={3}>
              <SummarySection title="Address">
                <Typography variant="caption" className={classes.summarySectionText}>{facility.address}</Typography>
                <Typography variant="caption" className={classes.summarySectionText}>{facility.city}, {facility.state} &nbsp; {facility.zip}</Typography>
              </SummarySection>
            </Grid>

            <Grid item xs={7} sm={4} md={3}>
              <SummarySection title="Manager">
                {facility.manager && (
                  <span className={classes.drawerActionLink}>
                    <AccountCircle className={classes.drawerActionIcon} /> &nbsp;
                    <Typography variant="caption" className={classes.summarySectionText}>{facility.manager}</Typography>
                  </span>
                )}
                {!facility.email && !facility.phone && !facility.manager && (
                  <Typography variant="caption" className={[classes.summarySectionText, classes.drawerActionText].join(' ')}>
                    No Manager Information Listed
                  </Typography>
                )}
                {facility.phone && (
                  <a href={`tel:${facility.phone}`} className={classes.drawerActionLink}>
                    <Phone className={classes.drawerActionIcon} /> &nbsp;
                    <Typography variant="caption" className={[classes.summarySectionText, classes.drawerActionText].join(' ')}>
                      {facility.phone}
                    </Typography>
                  </a>
                )}
                {facility.email && (
                  <a href={`mailto:${facility.email}`} className={classes.drawerActionLink}>
                    <Email className={classes.drawerActionIcon} />
                  &nbsp;
                  <Typography variant="caption" className={[classes.summarySectionText, classes.drawerActionText].join(' ')}>
                      {facility.email}
                    </Typography>
                  </a>
                )}
              </SummarySection>
            </Grid>

            {/* <Grid item xs={12}>
              <Divider variant="middle" className={classes.divider} />
            </Grid> */}

            <Grid item xs={12} md={3}>
              <SummarySection title="Status">
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', flex: 1, }}>
                    {statusIcon}
                    <div style={{ flex: 1, display: 'flex', alignItems: "baseline" }}>
                      <Typography variant="caption" className={classes.statusText}
                      >

                        {statusText}
                        <Tooltip title="A facility is Certified if it has no overdue CIPs, MCAPs or IAPs. A facility is Conditionally Certified if it has any IAPs, or if there are any CIPs/MCAPs overdue by 60 days or less. A facility is Conditionally Decertified if it has any CIPs, MCAPs or IAPs overdue by more than 60 days, or if an IAP has been found non-compliant after 3 follow-up visits.">
                          <Typography className={classes.statusButton}>
                            Learn more
                      </Typography>
                        </Tooltip>
                      </Typography>

                    </div>
                  </div>

                  <div>
                    {overdueIaps > 0 && (
                      <Typography className={classes.overdueText}>{overdueIaps} Overdue IAPs</Typography>
                    )}
                    {overdueMcaps > 0 && (
                      <Typography className={classes.overdueText}>{overdueMcaps} Overdue MCAPs</Typography>
                    )}
                    {overdueCips > 0 && (
                      <Typography className={classes.overdueText}>{overdueCips} Overdue CIPs</Typography>
                    )}
                    {statusMessage && (
                      <Typography className={classes.overdueText}>{statusMessage}</Typography>
                    )}
                  </div>
                </div>
              </SummarySection>
            </Grid>
          </Grid>
        </>
      )
    }

    </div >
  );

  return (
    <div className={classes.root}>
      {facilitySummary}

      <Container maxWidth="md" className={classes.content}>
        { followUps && followUps.length > 0
          ? <Paper elevation={1} className={classes.list}>
              <List>
                { fetchingFollowUps
                  ? (
                    <LoadingIndicator
                      classes={{
                        container: classes.loading,
                      }}>
                      Loading Follow-Ups
                    </LoadingIndicator>
                  ) : (
                    visibleFollowUps.map(fu => (
                      <FollowUpListItem key={fu.data.id} facilityId={facilityId} followUp={fu} form={forms[fu.type]!} />
                    ))
                  )
                }
              </List>
              <TablePagination
                className={classes.pagination}
                count={totalFollowUps}
                rowsPerPage={rowsPerPage}
                component="div"
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Paper>
          : <Alert severity="success">There are no remaining outstanding action plans.</Alert>
        }
      </Container>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      display: "flex",
      flexDirection: 'column',
      alignContent: "stretch",
      backgroundColor: theme.palette.background.default,
    },

    container: {
      marginTop: 20,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      [theme.breakpoints.up("xs")]: {
        overflowY: "auto",
      },
      WebkitOverflowScrolling: "touch",
    },
    drawer: {
      flexBasis: drawerWidth,
      zIndex: 0,
      height: "100%",

    },
    content: {
      marginTop: theme.spacing(3)
    },
    drawerPaper: {
      width: drawerWidth,

      overflow: 'scroll',

      position: "relative",
      top: "inherit",
      left: "inherit",
      bottom: 0,
      padding: 0,
      zIndex: 1,
      borderRight: 0,
    },
    drawerRoot: {
      display: "flex",
      flexDirection: 'column',
      flex: 1,
    },
    summary: {
      [theme.breakpoints.down("xs")]: {
        paddingTop: 12,
      },
      padding: theme.spacing(1, 3, 2),
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.getContrastText(theme.palette.primary.main),
    },
    navBar: {
      backgroundColor: theme.palette.primary.main,
      // ...theme.mixins.toolbar,
      color: theme.palette.getContrastText(theme.palette.primary.main),
    },
    menuButton: {
      color: 'inherit',
    },
    backIcon: {
      fontSize: 14,
    },
    backButton: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      textDecoration: "none",
      textTransform: 'none',
    },

    summarySection: {
      // marginTop: 16,
      flex: 1,
    },
    summarySectionTitle: {
    },
    summarySectionText: {
      // marginTop: 4,
      flex: 1,
      color: theme.palette.getContrastText(theme.palette.primary.main),
      wordBreak: 'break-word',
    },
    drawerActionLink: {
      display: 'flex',
      textDecoration: 'none',
      color: "inherit",
      alignItems: 'center',
      "&:hover": {
        color: theme.palette.grey[300],
      }
    },
    drawerActionIcon: {
      fontSize: 14,
      color: "inherit",
    },
    drawerActionText: {
      color: "inherit",
      // fontWeight: 'bold',
      verticalAlign: 'middle',
    },
    summarySectionContents: {
      marginLeft: 6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    overdueText: {
      marginLeft: 12,
      // ...theme.typography.subtitle2,
      ...theme.typography.caption,
    },

    navDivider: {
      margin: theme.spacing(0, 0, 2),
      // backgroundColor: theme.palette.grey[600],
    },

    facilityPrimary: {
      // textAlign: 'center',
    },
    facilitySecondary: {
      marginRight: theme.spacing(5),
    },
    divider: {
      margin: theme.spacing(2, 0),
      backgroundColor: theme.palette.grey[500],
    },

    drawerToC: {
      marginTop: 12,
      flex: 1,
      backgroundColor: theme.palette.background.paper,
    },

    facilityName: {
      margin: theme.spacing(2, 5),
    },
    list: {
      display: "flex",
      flexDirection: "column",
    },
    statusText: {
      flex: 1,
      display: 'flex',
      marginLeft: theme.spacing(1),
      ...theme.typography.subtitle1,
      alignItems: 'baseline',
    },
    statusButton: {
      marginLeft: theme.spacing(2),
      color: 'inherit',
      fontSize: 10,
      fontWeight: 'bold',
      cursor: 'help'
    },
    loading: {
      backgroundColor: "#fff",
    },
    pagination: {},

    warning: {
      color: theme.palette.warning.main,
    },
    error: {
      color: theme.palette.error.main,
    },

  })
);

export default FollowUpFacilityView;
